import {useCallback, useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {platformAPI} from "../../../api/axiosConfig";
import {ScopeClient, ScopeSelection} from "../components/ScopeSplitSelector";

// scope param format: JSON object, e.g.:
//   {"c":"<clientID>"}                          — drill into client, no scope selected
//   {"c":"<clientID>","s":"default"}            — client-level scope selected
//   {"c":"<clientID>","s":"<deptID>"}           — dept-level scope selected
//   {"c":"<clientID>","s":"<deptID>","p":2}     — dept-level + page 2

type ScopeJSON = {c: string; s?: string; p?: number};

function encodeScope(clientID: string | null, selection: string | null, page: number): string | null {
    if (!clientID) return null;
    const obj: ScopeJSON = {c: clientID};
    if (selection) obj.s = selection;
    if (page > 0) obj.p = page;
    return JSON.stringify(obj);
}

function parseScope(param: string): ScopeJSON | null {
    try { return JSON.parse(param); } catch { return null; }
}

type ScopeResult = {
    scope: ScopeSelection | null;
    drillClientID: string | null;
    drillClientName: string | null;
    deptPage: number;
};

export function useScopeQueryParams(opts?: {defaultClientID?: string | null}): {
    scope: ScopeSelection | null;
    drillClientID: string | null;
    drillClientName: string | null;
    deptPage: number;
    onSelect: (scope: ScopeSelection) => void;
    onDrill: (clientID: string, clientName: string) => void;
    onDeptPageChange: (page: number) => void;
    onClear: () => void;
} {
    const [searchParams, setSearchParams] = useSearchParams();
    const [result, setResult] = useState<ScopeResult>({scope: null, drillClientID: null, drillClientName: null, deptPage: 0});
    const [resolved, setResolved] = useState(false);

    useEffect(() => {
        const param = searchParams.get("scope");
        if (!param) {
            if (opts?.defaultClientID) {
                platformAPI.get<{id: string; name: string}>(`api/clients/${opts.defaultClientID}`)
                    .then(res => setResult({scope: null, drillClientID: res.data.id, drillClientName: res.data.name, deptPage: 0}))
                    .finally(() => setResolved(true));
            } else {
                setResolved(true);
            }
            return;
        }

        const parsed = parseScope(param);
        if (!parsed?.c) { setResolved(true); return; }

        const {c: clientID, s: selection, p: page = 0} = parsed;

        const resolve = async () => {
            try {
                const clientRes = await platformAPI.get<{id: string; name: string}>(`api/clients/${clientID}`);
                const client: ScopeClient = {id: clientRes.data.id, name: clientRes.data.name};

                if (!selection) {
                    setResult({scope: null, drillClientID: client.id, drillClientName: client.name, deptPage: page});
                } else if (selection === "default") {
                    setResult({scope: {client, department: null}, drillClientID: client.id, drillClientName: client.name, deptPage: page});
                } else {
                    try {
                        const deptRes = await platformAPI.get<{id: string; name: string}>(`api/departments/${selection}`);
                        const dept = {id: deptRes.data.id, name: deptRes.data.name};
                        setResult({scope: {client, department: dept as any}, drillClientID: client.id, drillClientName: client.name, deptPage: page});
                    } catch {
                        setResult({scope: {client, department: null}, drillClientID: client.id, drillClientName: client.name, deptPage: page});
                    }
                }
            } catch {
                setSearchParams(p => { p.delete("scope"); return p; }, {replace: true});
            } finally {
                setResolved(true);
            }
        };

        resolve();
    }, []); // only run on mount

    const setScope = useCallback((clientID: string | null, selection: string | null, page: number) => {
        setSearchParams(p => {
            const encoded = encodeScope(clientID, selection, page);
            if (encoded) p.set("scope", encoded); else p.delete("scope");
            return p;
        }, {replace: true});
    }, [setSearchParams]);

    const onSelect = useCallback((s: ScopeSelection) => {
        const clientID = s.client?.id ?? null;
        const selection = s.client ? (s.department ? s.department.id : "default") : null;
        setResult(prev => ({...prev, scope: s, drillClientID: clientID ?? prev.drillClientID, drillClientName: s.client?.name ?? prev.drillClientName, deptPage: 0}));
        setScope(clientID, selection, 0);
    }, [setScope]);

    const onDrill = useCallback((clientID: string, clientName: string) => {
        setResult(prev => ({...prev, drillClientID: clientID, drillClientName: clientName, deptPage: 0}));
        setScope(clientID, null, 0);
    }, [setScope]);

    const onDeptPageChange = useCallback((page: number) => {
        setResult(prev => {
            const clientID = prev.drillClientID;
            const selection = prev.scope?.client ? (prev.scope.department ? prev.scope.department.id : "default") : null;
            setScope(clientID, selection, page);
            return {...prev, deptPage: page};
        });
    }, [setScope]);

    const onClear = useCallback(() => {
        setResult({scope: null, drillClientID: null, drillClientName: null, deptPage: 0});
        setSearchParams(p => { p.delete("scope"); return p; }, {replace: true});
    }, [setSearchParams]);

    if (!resolved) return {scope: null, drillClientID: null, drillClientName: null, deptPage: 0, onSelect, onDrill, onDeptPageChange, onClear};
    return {scope: result.scope, drillClientID: result.drillClientID, drillClientName: result.drillClientName, deptPage: result.deptPage, onSelect, onDrill, onDeptPageChange, onClear};
}
